import { TransactionType } from '../../../application/types/transactionType';
import { getTransactionAlert } from '../../../common/utils/transactionValidation';
import { Card, CardContent, Typography, Divider, Box } from '@mui/material';

interface SummaryProps {
    selectedDrug: any;
    selectedStorage: any;
    quantityChanged: any;
    storageRemaining: any;
    selectedEmergency: any;
    emergencyRemaining: any;
    patientData: any;
    note: any;
    referensData: any;
    disposedQuantity: any;
    disposedCause: any;
    transactionType: TransactionType;
}

const Summary: React.FC<SummaryProps> = ({
    selectedDrug,
    selectedStorage,
    quantityChanged,
    storageRemaining,
    disposedQuantity,
    selectedEmergency,
    emergencyRemaining,
    patientData,
    note,
    referensData,
    disposedCause,
    transactionType,
}) => {
    return (
        <Box justifyContent="center">
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="100%"
                mb={2}
            >
                {getTransactionAlert(
                    transactionType,
                    selectedDrug,
                    selectedStorage,
                    quantityChanged,
                    storageRemaining,
                    selectedEmergency,
                    emergencyRemaining
                )}
            </Box>

            <Box display="flex" justifyContent="center">
                <Card
                    className="border-r-2 mb-4 w-full sm:w-auto"
                    sx={{ p: 0, minWidth: { xs: '100%', sm: 400 } }}
                >
                    <CardContent>
                        <Typography
                            variant="h5"
                            fontSize={{ xs: '1.125rem', sm: '1.5rem' }}
                            fontWeight={600}
                            gutterBottom
                        >
                            {selectedDrug
                                ? `${selectedDrug.drugName} (${selectedDrug.concentration} ${selectedDrug.concentrationUnit}, ${selectedDrug.quantity}ml)`
                                : 'Ingen läkemedel vald'}
                        </Typography>

                        {selectedStorage && (
                            <>
                                <Typography
                                    variant="h6"
                                    fontSize={{ xs: '1rem', sm: '1.25rem' }}
                                    fontWeight={500}
                                    gutterBottom
                                >
                                    {selectedStorage?.name}
                                </Typography>
                                {quantityChanged > 0 && (
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        mb={1}
                                        px={1}
                                        sx={{ flexWrap: 'wrap' }}
                                    >
                                        <Typography
                                            component="span"
                                            fontSize={{
                                                xs: '0.875rem',
                                                sm: '1rem',
                                            }}
                                        >
                                            <strong>
                                                {transactionType ===
                                                TransactionType.CheckIn
                                                    ? 'Tillförsel:'
                                                    : 'Uttag:'}
                                            </strong>
                                        </Typography>
                                        <Typography
                                            component="span"
                                            fontSize={{
                                                xs: '0.875rem',
                                                sm: '1rem',
                                            }}
                                        >
                                            {quantityChanged} x{' '}
                                            {selectedDrug?.quantity}{' '}
                                            {selectedDrug?.quantityUnit}
                                        </Typography>
                                    </Box>
                                )}
                                {disposedQuantity > 0 && (
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        mb={2}
                                        px={1}
                                        sx={{ flexWrap: 'wrap' }}
                                    >
                                        <Typography
                                            component="span"
                                            fontSize={{
                                                xs: '0.875rem',
                                                sm: '1rem',
                                            }}
                                        >
                                            <strong>Kassering:</strong>
                                        </Typography>
                                        <Typography
                                            component="span"
                                            fontSize={{
                                                xs: '0.875rem',
                                                sm: '1rem',
                                            }}
                                        >
                                            {disposedQuantity} x{' '}
                                            {selectedDrug?.quantity}{' '}
                                            {selectedDrug?.quantityUnit}
                                        </Typography>
                                    </Box>
                                )}
                                {storageRemaining > 0 && (
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        mb={2}
                                        px={1}
                                        sx={{ flexWrap: 'wrap' }}
                                    >
                                        <Typography
                                            component="span"
                                            fontSize={{
                                                xs: '0.875rem',
                                                sm: '1rem',
                                            }}
                                        >
                                            <strong>Behållning:</strong>
                                        </Typography>
                                        <Typography
                                            component="span"
                                            fontSize={{
                                                xs: '0.875rem',
                                                sm: '1rem',
                                            }}
                                        >
                                            {storageRemaining} x{' '}
                                            {selectedDrug?.quantity}{' '}
                                            {selectedDrug?.quantityUnit}
                                        </Typography>
                                    </Box>
                                )}
                                {disposedCause &&
                                    disposedCause.reasons &&
                                    disposedCause.reasons.length > 0 && (
                                        <Box
                                            display="flex"
                                            justifyContent="space-between"
                                            mb={2}
                                            px={1}
                                            sx={{ flexWrap: 'wrap' }}
                                        >
                                            <Typography
                                                component="span"
                                                fontSize={{
                                                    xs: '0.875rem',
                                                    sm: '1rem',
                                                }}
                                            >
                                                <strong>
                                                    Orsak till kassering:
                                                </strong>
                                            </Typography>
                                            <Typography
                                                component="span"
                                                fontSize={{
                                                    xs: '0.875rem',
                                                    sm: '1rem',
                                                }}
                                            >
                                                {disposedCause.reasons.join(
                                                    ', '
                                                )}
                                            </Typography>
                                        </Box>
                                    )}

                                <Divider sx={{ marginY: { xs: 1, sm: 2 } }} />
                            </>
                        )}

                        {selectedEmergency && (
                            <>
                                <Typography
                                    variant="h6"
                                    fontSize={{ xs: '1rem', sm: '1.25rem' }}
                                    fontWeight={500}
                                    gutterBottom
                                >
                                    {selectedEmergency?.name}
                                </Typography>
                                {emergencyRemaining > 0 && (
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        mb={2}
                                        px={1}
                                        sx={{ flexWrap: 'wrap' }}
                                    >
                                        <Typography
                                            component="span"
                                            fontSize={{
                                                xs: '0.875rem',
                                                sm: '1rem',
                                            }}
                                        >
                                            <strong>Behållning:</strong>
                                        </Typography>
                                        <Typography
                                            component="span"
                                            fontSize={{
                                                xs: '0.875rem',
                                                sm: '1rem',
                                            }}
                                        >
                                            {emergencyRemaining} x{' '}
                                            {selectedDrug?.quantity}{' '}
                                            {selectedDrug?.quantityUnit}
                                        </Typography>
                                    </Box>
                                )}
                                <Divider sx={{ marginY: { xs: 1, sm: 2 } }} />
                            </>
                        )}

                        {patientData &&
                            (patientData.firstname ||
                                patientData.lastname ||
                                patientData.pnr) && (
                                <>
                                    <Typography
                                        variant="h6"
                                        fontSize={{ xs: '1rem', sm: '1.25rem' }}
                                        fontWeight={500}
                                        gutterBottom
                                    >
                                        Patient
                                    </Typography>
                                    <Typography
                                        fontSize={{
                                            xs: '0.875rem',
                                            sm: '1rem',
                                        }}
                                    >
                                        {`${patientData.firstname || ''} ${patientData.lastname || ''}`.trim()}
                                    </Typography>
                                    <Typography
                                        fontSize={{
                                            xs: '0.875rem',
                                            sm: '1rem',
                                        }}
                                    >
                                        {patientData.pnr}
                                    </Typography>
                                    {note && (
                                        <Divider
                                            sx={{ marginY: { xs: 1, sm: 2 } }}
                                        />
                                    )}
                                </>
                            )}

                        {referensData?.journalId && (
                            <>
                                <Typography
                                    variant="h6"
                                    fontSize={{ xs: '1rem', sm: '1.25rem' }}
                                    fontWeight={500}
                                    gutterBottom
                                >
                                    Referens
                                </Typography>
                                <Typography
                                    fontSize={{ xs: '0.875rem', sm: '1rem' }}
                                >
                                    {referensData.journalId}
                                </Typography>
                                {note && (
                                    <Divider
                                        sx={{ marginY: { xs: 1, sm: 2 } }}
                                    />
                                )}
                            </>
                        )}

                        {note && (
                            <>
                                <Typography
                                    variant="h6"
                                    fontSize={{ xs: '1rem', sm: '1.25rem' }}
                                    fontWeight={500}
                                    gutterBottom
                                >
                                    {patientData || referensData
                                        ? 'Kommentar'
                                        : 'Anteckning'}
                                </Typography>
                                <Typography
                                    fontSize={{ xs: '0.875rem', sm: '1rem' }}
                                >
                                    {note}
                                </Typography>
                            </>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default Summary;
